// packages/shared/validation.ts
import { z } from 'zod';

/**
 * 和春技術學院（FIT）西元年份範圍（DB 只存西元年）
 * 創立 1989、停辦 2023 — 下拉與驗證只保留此區間
 */
export const GRAD_YEAR_MIN = 1989;
export const GRAD_YEAR_MAX = 2023;

/**
 * 正規化畢業年份 → 西元 number | null
 * - 空值 → null
 * - 民國年（1～200）→ 自動 +1911
 * - 字串四位年 → number
 */
export function normalizeGraduateYear(val: unknown): number | null {
  if (val === '' || val === undefined || val === null) return null;
  const n = typeof val === 'number' ? val : Number.parseInt(String(val).trim(), 10);
  if (!Number.isFinite(n)) return null;
  // 民國年常見範圍：民國 50～120 → 西元 1961～2031
  if (n >= 1 && n <= 200) return n + 1911;
  return Math.trunc(n);
}

/**
 * 畢業年份：表單常送來字串；空字串 → null
 * 只存西元年（6 月畢業，無需月日）
 */
export const graduateYearSchema = z.preprocess(
  (val) => normalizeGraduateYear(val),
  z
    .number({
      invalid_type_error: '請選擇有效的畢業年份',
      required_error: '請選擇畢業年份',
    })
    .int('畢業年份須為整數')
    .min(GRAD_YEAR_MIN, `畢業年份不可早於 ${GRAD_YEAR_MIN}`)
    .max(GRAD_YEAR_MAX, `畢業年份不可晚於 ${GRAD_YEAR_MAX}`)
    .nullable()
);

// ============================================
// 公開檔案
// ============================================
export const publicProfileSchema = z.object({
  id: z.string().uuid({ message: '無效的校友 ID' }),
  name: z.string().min(1, '請填寫姓名').max(50, '姓名最多 50 字'),
  department: z.string().max(50, '科系最多 50 字').nullable(),
  graduateYear: z.number().int().min(GRAD_YEAR_MIN).max(GRAD_YEAR_MAX).nullable(),
  currentCompany: z.string().max(100).nullable(),
  currentTitle: z.string().max(50).nullable(),
  avatarUrl: z.string().nullable(),
  /** 公開頁封面；空則前端用全站預設 */
  coverImageUrl: z.string().max(2000).nullable().optional(),
  cardImageUrl: z.string().max(2000).nullable().optional(),
  bio: z.string().max(500).nullable(),
  lineId: z.string().max(50).nullable(),
  /** 產業／產品關鍵字（公開可搜尋） */
  industry: z.string().max(200).nullable().optional(),
  /** 需要的東西／尋求資源（公開可搜尋） */
  lookingFor: z.string().max(300).nullable().optional(),
  /** 師長／教職員（可刊登；畢業年非必填） */
  isFaculty: z.boolean().optional().default(false),
  isPublic: z.boolean().default(true),
  email: z.string().email('Email 格式不正確').max(100).nullable().optional(),
  phone: z.string().max(20).nullable().optional(),
});

export type PublicProfile = z.infer<typeof publicProfileSchema>;

// ============================================
// 完整名片資料
// ============================================
export const myProfileSchema = z.object({
  name: z
    .string({ required_error: '請填寫姓名' })
    .min(1, '請填寫姓名')
    .max(50, '姓名最多 50 字'),
  department: z.string().max(50, '科系最多 50 字').optional().nullable().or(z.literal('')),
  graduateYear: graduateYearSchema,
  currentCompany: z.string().max(100, '公司名稱最多 100 字').optional().nullable().or(z.literal('')),
  currentTitle: z.string().max(50, '職稱最多 50 字').optional().nullable().or(z.literal('')),
  phone: z.string().max(20, '電話最多 20 字').optional().nullable().or(z.literal('')),
  email: z
    .union([
      z.literal(''),
      z.null(),
      z.string().email('Email 格式不正確').max(100, 'Email 最多 100 字'),
    ])
    .optional()
    .nullable(),
  lineId: z.string().max(50, 'LINE ID 最多 50 字').optional().nullable().or(z.literal('')),
  bio: z.string().max(500, '自我介紹最多 500 字').optional().nullable().or(z.literal('')),
  /** 產業／產品關鍵字，方便校友搜尋合作 */
  industry: z
    .string()
    .max(200, '產業／產品關鍵字最多 200 字')
    .optional()
    .nullable()
    .or(z.literal('')),
  /** 需要的東西／尋求資源（人脈、技術、通路等） */
  lookingFor: z
    .string()
    .max(300, '需求說明最多 300 字')
    .optional()
    .nullable()
    .or(z.literal('')),
  /** 勾選＝師長／教職員；未勾選＝一般校友（畢業生） */
  isFaculty: z.boolean().default(false),
  isPublic: z.boolean().default(true),
  // 允許相對路徑 /api/card/file/... 或完整 URL（含 LINE CDN 頭像）
  avatarUrl: z.string().max(2000).optional().nullable().or(z.literal('')),
  cardImageUrl: z.string().max(2000).optional().nullable().or(z.literal('')),
  coverImageUrl: z.string().max(2000).optional().nullable().or(z.literal('')),
  cardOcrText: z.string().optional().nullable(),
  /** 讀取時回傳；表單提交不需送出 */
  isVerified: z.boolean().optional(),
  id: z.string().uuid().optional(),
});

export type MyProfile = z.infer<typeof myProfileSchema>;

export const profileSubmitSchema = myProfileSchema.extend({
  cardOcrText: z.string().optional().nullable(),
});

export type ProfileSubmit = z.infer<typeof profileSubmitSchema>;

// ============================================
// OCR
// ============================================
export const ocrParsedSchema = z.object({
  name: z.string().optional(),
  company: z.string().optional(),
  title: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  address: z.string().optional(),
});

export const ocrResultSchema = z.object({
  text: z.string(),
  parsed: ocrParsedSchema.optional(),
});

export type OcrParsed = z.infer<typeof ocrParsedSchema>;
export type OcrResult = z.infer<typeof ocrResultSchema>;

// ============================================
// 名冊查詢
// ============================================
/** 名冊每頁筆數：手機 8、桌機 16（前端依螢幕傳入） */
export const DIRECTORY_PAGE_SIZE_MOBILE = 8;
export const DIRECTORY_PAGE_SIZE_DESKTOP = 16;

export const directoryQuerySchema = z.object({
  q: z.string().max(100).optional(),
  department: z.string().max(50).optional(),
  year: z.coerce.number().int().min(GRAD_YEAR_MIN).max(GRAD_YEAR_MAX).optional(),
  company: z.string().max(100).optional(),
  /** 產業／產品關鍵字篩選 */
  industry: z.string().max(100).optional(),
  /** 需求／尋求資源關鍵字篩選 */
  lookingFor: z.string().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(DIRECTORY_PAGE_SIZE_DESKTOP),
  sort: z.enum(['name', 'graduateYear', 'company', 'createdAt']).default('name'),
  order: z.enum(['asc', 'desc']).default('asc'),
});

export type DirectoryQuery = z.infer<typeof directoryQuerySchema>;

export const directoryResponseSchema = z.object({
  items: z.array(publicProfileSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  hasMore: z.boolean(),
});

export type DirectoryResponse = z.infer<typeof directoryResponseSchema>;

export const uploadSignResponseSchema = z.object({
  uploadUrl: z.string(),
  fields: z.record(z.string()),
  publicUrl: z.string(),
  key: z.string(),
});

export type UploadSignResponse = z.infer<typeof uploadSignResponseSchema>;

export const adminRoleSchema = z.enum(['owner', 'moderator']);
export type AdminRoleType = z.infer<typeof adminRoleSchema>;

export const adminSchema = z.object({
  id: z.string().uuid(),
  lineUserId: z.string().nullable(),
  email: z.string().email().nullable().or(z.literal('')).nullable(),
  name: z.string().nullable(),
  role: adminRoleSchema.default('owner'),
  alumniId: z.string().uuid().nullable().optional(),
  createdAt: z.number().int(),
});

/**
 * 新增管理員：優先從現有會員指派（alumniId）
 * 仍保留 lineUserId／email 後備（進階／例外）
 */
export const addAdminSchema = z
  .object({
    /** 從校友會員列表選取（推薦） */
    alumniId: z.string().uuid().optional().nullable(),
    lineUserId: z.string().min(1).optional().nullable(),
    email: z.string().email('Email 格式不正確').optional().nullable(),
    name: z.string().max(50).optional().nullable(),
    /** owner=主管理者；moderator=副管理者（僅審核） */
    role: adminRoleSchema.default('moderator'),
  })
  .refine((data) => data.alumniId || data.lineUserId || data.email, {
    message: '請從會員列表選擇，或填寫 LINE User ID／Email',
    path: ['alumniId'],
  });

export type Admin = z.infer<typeof adminSchema>;
export type AddAdminInput = z.infer<typeof addAdminSchema>;

export const adminAlumniSchema = publicProfileSchema.extend({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  lineUserId: z.string().nullable(),
  isVerified: z.boolean(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});

export type AdminAlumni = z.infer<typeof adminAlumniSchema>;

export const adminListResponseSchema = z.object({
  items: z.array(adminAlumniSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  hasMore: z.boolean(),
});

export type AdminListResponse = z.infer<typeof adminListResponseSchema>;

export const settingSchema = z.object({
  key: z.string().min(1).max(100),
  value: z.string(),
  description: z.string().optional(),
});

export type Setting = z.infer<typeof settingSchema>;

/** 產生畢業年份下拉選項（新→舊，西元年） */
export function graduateYearOptions(from = GRAD_YEAR_MIN, to = GRAD_YEAR_MAX): number[] {
  const years: number[] = [];
  for (let y = to; y >= from; y--) years.push(y);
  return years;
}

/** 西元 → 民國：民國年 = 西元年 - 1911 */
export function toRocYear(adYear: number): number {
  return adYear - 1911;
}

/** 民國 → 西元：西元年 = 民國年 + 1911 */
export function toAdYear(rocYear: number): number {
  return rocYear + 1911;
}

/**
 * 顯示用：西元 2010 ／ 民國 99
 * DB 仍只存西元年
 */
export function formatGraduateYear(adYear: number | null | undefined): string {
  if (adYear == null || !Number.isFinite(adYear)) return '';
  const roc = toRocYear(adYear);
  return `西元 ${adYear} ／ 民國 ${roc}`;
}

/** 下拉選項文字 */
export function formatGraduateYearOption(adYear: number): string {
  const roc = toRocYear(adYear);
  return `西元 ${adYear} 年（民國 ${roc} 年）`;
}
